import { useEffect, useState } from 'react';
import type { CalcRun, Entity } from '@ave/shared';
import { api, type CapTableForEntity } from '../lib/api';
import { money } from '../lib/format';
import { Badge, Card, EmptyState, ErrorNotice, SectionHeading, Spinner } from './ui';
import { Icon } from './icons';

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
const SELECT =
  'mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

const count = (value: string): string => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString('en-GB', { maximumFractionDigits: 0 }) : value;
};

const pct = (value: string): string => {
  const n = Number(value);
  return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : value;
};

/**
 * The capital structure, and the equity value split across it.
 *
 * Its own tab rather than a card under the instruments, because it is a
 * different kind of object and answers a different question. The instruments
 * library records what a holder holds and prices each one on its own. A share
 * class cannot be valued that way at all: a preference is worth what it is
 * worth because of what ranks above and below it, and "the equity is worth
 * £8m" is the input to the question rather than an answer to it.
 *
 * **The breakpoints are the argument, so they are on the page.** A reader who
 * is told the ordinary shares are worth £2.03m has been given a number. A
 * reader who can see that nothing reaches them below £5m, that they take
 * every pound between £5m and £8m, and that they share six sevenths above it,
 * has been given the reasoning — and every one of those boundaries can be
 * checked by hand against the articles. That is the whole claim of the
 * method, and hiding it behind a total would waste it.
 *
 * **Pro rata sits beside the allocation.** The figure a valuer is arguing
 * against is almost always "surely it is just their percentage", so the
 * answer to it belongs in the same row rather than in a paragraph underneath.
 */
export function CapTable({
  engagementId,
  entities,
  canWrite,
  currency,
}: {
  engagementId: string;
  entities: Entity[];
  canWrite: boolean;
  currency: string;
}) {
  const [tables, setTables] = useState<CapTableForEntity[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const res = await api.capTable(engagementId);
      setTables(res.capTables);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the cap table');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  if (error) return <ErrorNotice title="Cap table" message={error} />;
  if (tables === null) return <Spinner label="Loading the cap table" />;

  return (
    <div className="space-y-6">
      {entities.map((entity) => (
        <EntityCapTable
          key={entity.id}
          engagementId={engagementId}
          entity={entity}
          table={
            tables.find((t) => t.entityId === entity.id) ?? {
              entityId: entity.id,
              classes: [],
              pool: [],
              latestAllocation: null,
            }
          }
          canWrite={canWrite}
          currency={currency}
          onChange={load}
        />
      ))}
    </div>
  );
}

function EntityCapTable({
  engagementId,
  entity,
  table,
  canWrite,
  currency,
  onChange,
}: {
  engagementId: string;
  entity: Entity;
  table: CapTableForEntity;
  canWrite: boolean;
  currency: string;
  onChange: () => void | Promise<void>;
}) {
  const [adding, setAdding] = useState<'class' | 'tranche' | null>(null);
  const [allocating, setAllocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasOrdinary = table.classes.some((c) => c.kind === 'ordinary');
  const totalShares = table.classes.reduce((sum, c) => sum + Number(c.shares), 0);

  async function remove(kind: 'share-classes' | 'option-tranches', rowId: string) {
    setError(null);
    try {
      await api.removeCapTableRow(engagementId, kind, rowId);
      await onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove it');
    }
  }

  return (
    <Card>
      <SectionHeading
        title={entity.name}
        subtitle="The rungs of the structure, as the articles set them out"
        right={
          table.classes.length > 0 ? (
            <Badge tone="slate">{count(String(totalShares))} shares as converted</Badge>
          ) : null
        }
      />

      {table.classes.length === 0 ? (
        <EmptyState
          title="No structure recorded"
          detail="A share class is not an instrument: it cannot be valued on its own, because a preference is worth what it is worth only in relation to what ranks above and below it. Record every class, and the option pricing method allocates across all of them at once."
        />
      ) : (
        <div className="overflow-x-auto px-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-3 font-medium">Class</th>
                <th className="py-2 pr-3 text-right font-medium">Shares</th>
                <th className="py-2 pr-3 text-right font-medium">Preference</th>
                <th className="py-2 pr-3 font-medium">Rank</th>
                <th className="py-2 pr-3 font-medium">Participation</th>
                {canWrite ? <th className="py-2" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {table.classes.map((c) => (
                <tr key={c.id}>
                  <td className="py-2 pr-3 text-slate-800">
                    {c.name}
                    {c.citationRefs.length > 0 ? (
                      <span className="ml-2 text-xs text-slate-400">{c.citationRefs.join(', ')}</span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                    {count(c.shares)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                    {c.liquidationPreference === null ? '—' : money(c.liquidationPreference)}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">
                    {c.seniority === null ? 'behind every preference' : `rank ${c.seniority}`}
                  </td>
                  <td className="py-2 pr-3 text-slate-600">
                    {c.kind === 'ordinary'
                      ? '—'
                      : c.participating
                        ? c.participationCap === null
                          ? 'participates, uncapped'
                          : `participates, capped at ${money(c.participationCap)}`
                        : 'non-participating'}
                  </td>
                  {canWrite ? (
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        className="text-xs text-slate-400 hover:text-rose-600"
                        onClick={() => remove('share-classes', c.id)}
                      >
                        Remove
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>

          {table.pool.length > 0 ? (
            <>
              <p className="mt-5 text-sm font-medium text-slate-800">Options and warrants</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-500">
                Kept apart from the classes because a pool changes the answer twice over: the
                shares dilute, and the strike money comes into the company on exercise. Entered as
                shares already in issue it would take the dilution without the consideration.
              </p>
              <table className="mt-2 w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {table.pool.map((p) => (
                    <tr key={p.id}>
                      <td className="py-2 pr-3 text-slate-800">{p.name}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                        {count(p.options)} {p.kind}s
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                        at {money(p.strike)}
                      </td>
                      {canWrite ? (
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            className="text-xs text-slate-400 hover:text-rose-600"
                            onClick={() => remove('option-tranches', p.id)}
                          >
                            Remove
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}
        </div>
      )}

      {table.classes.length > 0 && !hasOrdinary ? (
        <p className="mt-3 flex gap-2 px-6 text-sm leading-relaxed text-amber-700">
          <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Every class recorded is preferred. The preferences rank ahead of something and the
            surplus above them belongs to somebody — the ordinary shares are missing.
          </span>
        </p>
      ) : null}

      {error ? <p className="mt-3 px-6 text-sm text-rose-600">{error}</p> : null}

      {canWrite ? (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 px-6 py-4">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setAdding(adding === 'class' ? null : 'class')}
          >
            Add a share class
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setAdding(adding === 'tranche' ? null : 'tranche')}
          >
            Add options or warrants
          </button>
          {table.classes.length > 0 && hasOrdinary ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setAllocating((v) => !v)}
            >
              {table.latestAllocation ? 'Allocate again' : 'Allocate an equity value'}
            </button>
          ) : null}
        </div>
      ) : null}

      {adding === 'class' ? (
        <AddShareClass
          engagementId={engagementId}
          entityId={entity.id}
          onClose={() => setAdding(null)}
          onSaved={async () => {
            setAdding(null);
            await onChange();
          }}
        />
      ) : null}

      {adding === 'tranche' ? (
        <AddOptionTranche
          engagementId={engagementId}
          entityId={entity.id}
          onClose={() => setAdding(null)}
          onSaved={async () => {
            setAdding(null);
            await onChange();
          }}
        />
      ) : null}

      {allocating ? (
        <AllocateForm
          engagementId={engagementId}
          entityId={entity.id}
          onClose={() => setAllocating(false)}
          onSaved={async () => {
            setAllocating(false);
            await onChange();
          }}
        />
      ) : null}

      {table.latestAllocation ? (
        <Allocation run={table.latestAllocation} currency={currency} />
      ) : null}
    </Card>
  );
}

/**
 * What the allocation produced, with the reasoning above the answer.
 *
 * The slices come first because they are what makes the totals checkable.
 * Each boundary is a value somebody can test against the articles by hand,
 * and each split is the share of the next pound that changes hands there.
 */
interface AllocatedClass {
  name: string;
  allocated_value: string;
  value_per_share_precise: string;
  pro_rata_value: string;
}

interface AllocatedTranche {
  name: string;
  kind: string;
  options: string;
  strike: string;
  allocated_value: string;
  value_per_option_precise: string;
  exercises_at_this_value: boolean;
}

function Allocation({ run, currency }: { run: CalcRun; currency: string }) {
  const outputs = run.outputs as Record<string, any>;
  const classes = (outputs.classes ?? []) as AllocatedClass[];
  const pool = (outputs.option_pool ?? []) as AllocatedTranche[];
  const slices = (outputs.breakpoints ?? []) as {
    from: string;
    to: string | null;
    value: string;
    shares: Record<string, string>;
  }[];

  return (
    <div className="border-t border-slate-100 px-6 py-5">
      <p className="text-sm font-medium text-slate-800">
        {money(outputs.equity_value)} allocated across the structure
      </p>
      <p className="mt-1 text-sm leading-relaxed text-slate-500">
        Option pricing method at a {pct(outputs.volatility)} volatility, exit assumed{' '}
        {outputs.exit_date}, risk-free {pct(outputs.risk_free_rate)}. Each breakpoint is priced as
        a call on the equity struck there, so the whole value is divided and none of it created.
      </p>

      <p className="mt-4 text-sm font-medium text-slate-800">
        Where the split changes, and what changes hands
      </p>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-3 font-medium">Slice</th>
              <th className="py-2 pr-3 text-right font-medium">Worth</th>
              <th className="py-2 font-medium">Share of the next pound</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {slices.map((slice) => (
              <tr key={slice.from}>
                <td className="py-2 pr-3 whitespace-nowrap tabular-nums text-slate-700">
                  {money(slice.from)} {slice.to === null ? 'upwards' : `to ${money(slice.to)}`}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                  {money(slice.value)}
                </td>
                <td className="py-2 text-slate-600">
                  {Object.entries(slice.shares)
                    .filter(([, share]) => Number(share) > 0)
                    .map(([name, share]) => `${name} ${pct(share)}`)
                    .join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-5 text-sm font-medium text-slate-800">What each class is worth</p>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-3 font-medium">Class</th>
              <th className="py-2 pr-3 text-right font-medium">Allocated</th>
              <th className="py-2 pr-3 text-right font-medium">Per share</th>
              {/* The figure a valuer is arguing against, in the same row as
                  the answer to it. */}
              <th className="py-2 text-right font-medium">If it divided pro rata</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {classes.map((row) => (
              <tr key={row.name}>
                <td className="py-2 pr-3 text-slate-800">{row.name}</td>
                <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                  {money(row.allocated_value)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                  {money(row.value_per_share_precise)}
                </td>
                <td className="py-2 text-right tabular-nums text-slate-400">
                  {money(row.pro_rata_value)}
                </td>
              </tr>
            ))}
            {pool.map((row) => (
              <tr key={row.name}>
                <td className="py-2 pr-3 text-slate-800">
                  {row.name}
                  <span className="ml-2 text-xs text-slate-400">
                    {count(row.options)} {row.kind}s at {money(row.strike)}
                    {/* Whether it is worth exercising at this equity value, which
                        is not whether it is worth anything: the method prices the
                        exits at which it is in the money. */}
                    {row.exercises_at_this_value ? ' · exercised here' : ' · under water here'}
                  </span>
                </td>
                <td className="py-2 pr-3 text-right tabular-nums text-slate-900">
                  {money(row.allocated_value)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums text-slate-700">
                  {money(row.value_per_option_precise)}
                </td>
                <td className="py-2 text-right text-slate-400">—</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-200">
              <td className="py-2 pr-3 font-medium text-slate-800">Total allocated</td>
              <td className="py-2 pr-3 text-right font-medium tabular-nums text-slate-900">
                {money(outputs.total_allocated)}
              </td>
              <td colSpan={2} className="py-2 text-right text-xs text-slate-400">
                equal to the equity value, as it must be
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {(outputs.converts_at_this_value ?? []).length > 0 ? (
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          At this equity value {(outputs.converts_at_this_value as string[]).join(', ')} would give
          up the preference and convert. That decision is taken again at every breakpoint, which is
          why a class can hold its preference low down and convert higher up.
        </p>
      ) : null}

      <p className="mt-3 text-xs text-slate-400">
        {currency} · engine {run.engineVersion}
        {(run.libraryVersions as Record<string, string>)?.quantlib
          ? `, QuantLib ${(run.libraryVersions as Record<string, string>).quantlib}`
          : ''}
      </p>
    </div>
  );
}

function AddShareClass({
  engagementId,
  entityId,
  onClose,
  onSaved,
}: {
  engagementId: string;
  entityId: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'ordinary' | 'preferred'>('preferred');
  const [shares, setShares] = useState('');
  const [preference, setPreference] = useState('');
  const [seniority, setSeniority] = useState('1');
  const [participating, setParticipating] = useState(false);
  const [cap, setCap] = useState('');
  const [citation, setCitation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid =
    name.trim() !== '' &&
    shares.trim() !== '' &&
    (kind === 'ordinary' || (preference.trim() !== '' && seniority.trim() !== ''));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.createShareClass(engagementId, {
        entityId,
        name: name.trim(),
        kind,
        shares: shares.trim(),
        ...(kind === 'preferred'
          ? {
              liquidationPreference: preference.trim(),
              seniority: Number(seniority),
              participating,
              ...(participating && cap.trim() !== '' ? { participationCap: cap.trim() } : {}),
            }
          : {}),
        citationRefs: citation.trim() === '' ? [] : [citation.trim()],
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the class');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-slate-100 px-6 py-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          What the articles call it
          <input
            className={INPUT}
            value={name}
            placeholder="Series A Preferred"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Kind
          <select
            className={SELECT}
            value={kind}
            onChange={(e) => setKind(e.target.value as 'ordinary' | 'preferred')}
          >
            <option value="preferred">Preferred — ranks ahead of the ordinary</option>
            <option value="ordinary">Ordinary — ranks behind every preference</option>
          </select>
        </label>

        <label className="text-sm text-slate-600">
          Shares, as converted
          <input
            className={INPUT}
            inputMode="decimal"
            value={shares}
            placeholder="1500000"
            onChange={(e) => setShares(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          The article that creates it
          <input
            className={INPUT}
            value={citation}
            placeholder="articles:4.2 — optional, and the first thing asked about a preference"
            onChange={(e) => setCitation(e.target.value)}
          />
        </label>
      </div>

      {kind === 'preferred' ? (
        <>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-sm text-slate-600">
              Liquidation preference
              <input
                className={INPUT}
                inputMode="decimal"
                value={preference}
                placeholder="1500000"
                onChange={(e) => setPreference(e.target.value)}
              />
            </label>

            <label className="text-sm text-slate-600">
              Rank — 1 is paid first
              <input
                className={INPUT}
                inputMode="numeric"
                value={seniority}
                onChange={(e) => setSeniority(e.target.value)}
              />
            </label>
          </div>

          <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={participating}
              onChange={(e) => setParticipating(e.target.checked)}
            />
            Participates in the surplus after taking its preference
          </label>

          {participating ? (
            <label className="mt-2 block text-sm text-slate-600 sm:w-1/2">
              Capped at, in total
              <input
                className={INPUT}
                inputMode="decimal"
                value={cap}
                placeholder="Leave blank for uncapped"
                onChange={(e) => setCap(e.target.value)}
              />
            </label>
          ) : null}

          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            Nil is a legitimate preference and means the class ranks for nothing. Leaving it blank
            means nobody has read the article, which is a different statement.
          </p>
        </>
      ) : null}

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          Record the class
        </button>
      </div>
    </div>
  );
}

function AddOptionTranche({
  engagementId,
  entityId,
  onClose,
  onSaved,
}: {
  engagementId: string;
  entityId: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'option' | 'warrant'>('option');
  const [options, setOptions] = useState('');
  const [strike, setStrike] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = name.trim() !== '' && options.trim() !== '' && strike.trim() !== '';

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.createOptionTranche(engagementId, {
        entityId,
        name: name.trim(),
        kind,
        options: options.trim(),
        strike: strike.trim(),
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the tranche');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-slate-100 px-6 py-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          What to call it
          <input
            className={INPUT}
            value={name}
            placeholder="2024 EMI pool"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Kind
          <select
            className={SELECT}
            value={kind}
            onChange={(e) => setKind(e.target.value as 'option' | 'warrant')}
          >
            <option value="option">Options</option>
            <option value="warrant">Warrants</option>
          </select>
        </label>

        <label className="text-sm text-slate-600">
          Shares under the grant
          <input
            className={INPUT}
            inputMode="decimal"
            value={options}
            placeholder="1000000"
            onChange={(e) => setOptions(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Exercise price per share
          <input
            className={INPUT}
            inputMode="decimal"
            value={strike}
            placeholder="0.50"
            onChange={(e) => setStrike(e.target.value)}
          />
        </label>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-slate-500">
        Only grants that will be exercisable at the exit. Vesting and leaver provisions are not
        modelled: whether a grant will have vested by then is a fact about the grant, and options
        that will have lapsed do not belong in the structure at all.
      </p>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          Record it
        </button>
      </div>
    </div>
  );
}

/**
 * The four things the articles do not say.
 *
 * Asked for at the moment they are used and never stored on the structure,
 * for the same reason a volatility is not stored on an instrument: they are
 * chosen by somebody who has to defend them, and a value that sits on the
 * cap table would be read later as something the articles said.
 */
function AllocateForm({
  engagementId,
  entityId,
  onClose,
  onSaved,
}: {
  engagementId: string;
  entityId: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [equityValue, setEquityValue] = useState('');
  const [exitDate, setExitDate] = useState('');
  const [volatility, setVolatility] = useState('');
  const [riskFreeRate, setRiskFreeRate] = useState('');
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid =
    equityValue.trim() !== '' &&
    /^\d{4}-\d{2}-\d{2}$/.test(exitDate) &&
    volatility.trim() !== '' &&
    riskFreeRate.trim() !== '';

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.allocateEquity(engagementId, {
        entityId,
        equityValue: equityValue.trim(),
        exitDate,
        volatility: volatility.trim(),
        riskFreeRate: riskFreeRate.trim(),
        evidenceRefs: evidence.trim() === '' ? [] : evidence.split(',').map((s) => s.trim()),
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The allocation was refused');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-5">
      <p className="text-sm font-medium text-slate-800">What the articles do not say</p>
      <p className="mt-1 text-sm leading-relaxed text-slate-500">
        None of these four is in the structure above. They are chosen for this allocation and
        recorded with it, the same way a volatility is chosen when an instrument is priced.
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          Equity value to divide
          <input
            className={INPUT}
            inputMode="decimal"
            value={equityValue}
            placeholder="10000000"
            onChange={(e) => setEquityValue(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Assumed exit
          <input
            className={INPUT}
            type="date"
            value={exitDate}
            onChange={(e) => setExitDate(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Volatility (annualised, as a decimal)
          <input
            className={INPUT}
            inputMode="decimal"
            value={volatility}
            placeholder="0.50"
            onChange={(e) => setVolatility(e.target.value)}
          />
        </label>

        <label className="text-sm text-slate-600">
          Risk-free rate (as a decimal)
          <input
            className={INPUT}
            inputMode="decimal"
            value={riskFreeRate}
            placeholder="0.04"
            onChange={(e) => setRiskFreeRate(e.target.value)}
          />
        </label>
      </div>

      <label className="mt-3 block text-sm text-slate-600">
        Evidence for them
        <input
          className={INPUT}
          value={evidence}
          placeholder="evidence_point:series-b-post-money, evidence_point:comparable-vol"
          onChange={(e) => setEvidence(e.target.value)}
        />
      </label>

      <p className="mt-3 text-sm leading-relaxed text-slate-500">
        The exit horizon is an assumption about when the preferences are tested, and it moves the
        answer rather than just a total. So does the volatility: it decides how much of the value
        sits above each breakpoint.
      </p>

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Allocating…' : 'Allocate'}
        </button>
      </div>
    </div>
  );
}
